import { useState, useRef, useCallback, useLayoutEffect } from 'react';

import { createPopper, Instance, Placement } from '@popperjs/core';
import isEqual from 'lodash.isequal';

import { useMounted } from '@adsk/alloy-react-helpers';

export default function usePopper({
  placement: initialPlacement = 'bottom',
  offset: offsetProp,
  preventOverflow = true,
  fixed = false,
  show = true,
  flip = true,
  eventsEnabled = true,
  targetElement,
  overlayElement,
  arrowElement,
  layoutDependency,
}: {
  placement?: Placement;
  offset?: number[];
  preventOverflow?: boolean;
  fixed?: boolean;
  show?: boolean;
  flip?: boolean;
  eventsEnabled?: boolean;
  targetElement?: Element | null;
  overlayElement?: HTMLElement;
  arrowElement?: Element;
  layoutDependency: unknown;
}) {
  const isMountedRef = useMounted();
  const popper = useRef<Instance | null>(null);
  const [placement, setPlacement] = useState<Placement>(initialPlacement);
  const [popoverStyles, setPopoverStyles] = useState({});
  const [arrowStyles, setArrowStyles] = useState({});

  useLayoutEffect(() => {
    if (targetElement && overlayElement) {
      popper.current = createPopper(targetElement, overlayElement, {
        placement: initialPlacement,
        strategy: fixed ? 'fixed' : 'absolute',
        modifiers: [
          {
            name: 'eventListeners',
            enabled: eventsEnabled,
          },
          {
            name: 'applyStyles',
            enabled: false,
          },
          {
            name: 'flip',
            enabled: flip,
            options: { padding: 0 },
          },
          {
            name: 'computeStyles',
            options: { gpuAcceleration: false, adaptive: false },
          },
          {
            name: 'offset',
            options: { offset: offsetProp },
          },
          {
            name: 'preventOverflow',
            enabled: preventOverflow,
            options: {
              tetherOffset: () => arrowElement?.clientWidth || 0,
            },
          },
          {
            name: 'arrow',
            enabled: Boolean(arrowElement),
            options: { element: arrowElement },
          },
          {
            name: 'updateState',
            phase: 'write',
            enabled: true,
            fn({ state }) {
              if (isMountedRef.current) {
                setPlacement(state.placement);
                setPopoverStyles((current) =>
                  isEqual(current, state.styles.popper)
                    ? current
                    : state.styles.popper
                );
                setArrowStyles((current) =>
                  isEqual(current, state.styles.arrow)
                    ? current
                    : state.styles.arrow
                );
              }
            },
          },
        ],
      });
    }
    return () => {
      if (popper.current) {
        popper.current.destroy();
        popper.current = null;
      }
    };
  }, [
    eventsEnabled,
    fixed,
    flip,
    show,
    preventOverflow,
    targetElement,
    overlayElement,
    arrowElement,
    initialPlacement,
    isMountedRef,
    offsetProp,
    layoutDependency,
  ]);

  const update = useCallback(() => {
    if (popper.current) {
      popper.current.forceUpdate();
      return true;
    }
    return false;
  }, []);

  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      if (show) update();
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [show, update]);

  return {
    update,
    placement,
    styles: {
      overlay: popoverStyles,
      arrow: arrowStyles,
    },
  };
}
